import type {
  ConnectorMeta,
  ConnectorPermissionLevel,
} from '@research-assistant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2Icon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { DialogClose } from '@/components/ui/dialog.js';
import { notify } from '@/lib/notify.js';

import { useInvalidateConnections } from '../../hooks/useConnections.js';
import { useOAuthConnect } from '../../hooks/useOAuthConnect.js';
import * as api from '../../services/integration-api.js';
import type {
  ConnectionCreateDetails,
  ConnectorConnection,
  ExternalTenantConsent,
} from '../../services/integration-api.js';

import { AtlassianCredentialsForm } from './AtlassianCredentialsForm.js';
import { AzureDevOpsCredentialsForm } from './AzureDevOpsCredentialsForm.js';
import { BrowserStackCredentialsForm } from './BrowserStackCredentialsForm.js';
import {
  ConnectorAccessDialog,
  defaultConnectorAccess,
  storedConnectorAccess,
  type ConnectorAccess,
} from './ConnectorAccessDialog.js';
import { CursorCredentialsForm } from './CursorCredentialsForm.js';
import { DatabricksCredentialsForm } from './DatabricksCredentialsForm.js';
import { Dynamics365CredentialsForm } from './Dynamics365CredentialsForm.js';
import { GithubCredentialsForm } from './GithubCredentialsForm.js';
import { GitlabCredentialsForm } from './GitlabCredentialsForm.js';
import { ReportPortalCredentialsForm } from './ReportPortalCredentialsForm.js';
import { SharePointTenantChoiceDialog } from './SharePointTenantChoiceDialog.js';
import { TestRailCredentialsForm } from './TestRailCredentialsForm.js';
import { XrayCredentialsForm } from './XrayCredentialsForm.js';
import { ZephyrCredentialsForm } from './ZephyrCredentialsForm.js';
import { ConnectionMethodDialog } from './ConnectionMethodDialog.js';
import {
  ConnectionSetupDialog,
  type ConnectionSetupResult,
} from './ConnectionSetupDialog.js';
import { useConnectorScope } from './connector-scope.js';

/**
 * Inline credentials forms keyed by connector id. Every form collects a credential and nothing else —
 * the choice between a sign-in and a token is made before any of them opens, in the connection-method
 * dialog, so none of them reads the connector's `meta` any more.
 *
 * `afterSignIn` says the OAuth popup has already run. Only Azure DevOps acts on it: its form still has
 * a step left after the popup, because every connection pins an organization.
 */
const CREDENTIALS_FORMS: Partial<
  Record<
    string,
    (props: {
      onSaved: () => void;
      onCancel: () => void;
      details?: ConnectionCreateDetails;
      afterSignIn?: boolean;
      explainUnavailableActions?: boolean;
      onBusyChange?: (busy: boolean) => void;
    }) => JSX.Element
  >
> = {
  jira: (props) => (
    <AtlassianCredentialsForm
      explainUnavailableActions={props.explainUnavailableActions}
      onBusyChange={props.onBusyChange}
      provider="jira"
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  confluence: (props) => (
    <AtlassianCredentialsForm
      explainUnavailableActions={props.explainUnavailableActions}
      onBusyChange={props.onBusyChange}
      provider="confluence"
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  testrail: (props) => (
    <TestRailCredentialsForm
      explainUnavailableActions={props.explainUnavailableActions}
      onBusyChange={props.onBusyChange}
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  zephyr: (props) => (
    <ZephyrCredentialsForm
      explainUnavailableActions={props.explainUnavailableActions}
      onBusyChange={props.onBusyChange}
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  azure_devops: AzureDevOpsCredentialsForm,
  github: GithubCredentialsForm,
  gitlab: (props) => (
    <GitlabCredentialsForm
      explainUnavailableActions={props.explainUnavailableActions}
      onBusyChange={props.onBusyChange}
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  browserstack: (props) => (
    <BrowserStackCredentialsForm
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  cursor: (props) => (
    <CursorCredentialsForm
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  xray: (props) => (
    <XrayCredentialsForm
      explainUnavailableActions={props.explainUnavailableActions}
      onBusyChange={props.onBusyChange}
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  dynamics365: (props) => (
    <Dynamics365CredentialsForm
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  databricks: (props) => (
    <DatabricksCredentialsForm
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
  reportportal: (props) => (
    <ReportPortalCredentialsForm
      onSaved={props.onSaved}
      onCancel={props.onCancel}
      details={props.details}
    />
  ),
};

/** These forms reconcile connection queries independently of their dialog's lifetime. */
const CREDENTIALS_REFRESH_CONNECTIONS: ReadonlySet<string> = new Set([
  'jira',
  'confluence',
  'azure_devops',
  'github',
  'gitlab',
  'testrail',
  'zephyr',
  'xray',
]);

/**
 * Connectors whose shared connect asks whose directory it is first — ours or a customer's. Only
 * SharePoint reaches another tenant, and the two paths differ (§14): ours connects by signing in, a
 * customer's needs their administrator's approval before anyone can.
 */
const TENANT_CHOICE_CONNECTORS: ReadonlySet<string> = new Set(['sharepoint']);

/** What the connection-method dialog says about one provider that takes either a sign-in or a token. */
interface ConnectionMethods {
  /**
   * Whose sign-in the popup runs. Azure DevOps authorizes through Microsoft rather than through
   * itself, so this is not always the provider's own name.
   */
  signInWith: string;
  /** What the provider calls its token, article included, so the dialog's lines read as sentences. */
  tokenLabel: string;
  /**
   * Whether the credentials form still has a step after the popup. Azure DevOps pins an organization
   * on every connection and the popup cannot ask for it, so that form opens once the sign-in is done.
   */
  signInContinuesInForm?: boolean;
}

/**
 * Providers that accept both a sign-in and a token, and the words that choice is offered in. An entry
 * here is what puts the method dialog in front of the credentials form; a provider with one way in
 * has none and opens its form straight away.
 */
const CONNECTION_METHODS: Partial<Record<string, ConnectionMethods>> = {
  jira: { signInWith: 'Atlassian', tokenLabel: 'an API token' },
  confluence: { signInWith: 'Atlassian', tokenLabel: 'an API token' },
  github: { signInWith: 'GitHub', tokenLabel: 'a personal access token' },
  azure_devops: {
    signInWith: 'Microsoft',
    tokenLabel: 'a personal access token',
    signInContinuesInForm: true,
  },
};

/**
 * The caches beside presence that also carry a connection's access. A saved change refreshes them, or a
 * surface that reads one of them, such as the OneDrive reconnect, sends the old access back.
 */
const ACCESS_CACHE_KEYS = [
  ['connectors-catalog'],
  ['workspace-connectors'],
  ['onedrive', 'account'],
] as const;

/**
 * The name and instruction an OAuth connect carries, as `useOAuthConnect` takes them. Absent details or
 * blank fields contribute nothing, so a first connect sends the same request it always did.
 */
function connectDetailsArgs(details: ConnectionCreateDetails | null): {
  label?: string;
  usageHint?: string;
} {
  if (!details) return {};
  return {
    ...(details.label?.trim() ? { label: details.label.trim() } : {}),
    ...(details.usageHint?.trim()
      ? { usageHint: details.usageHint.trim() }
      : {}),
  };
}

/** What the connect flow needs to know about the connector and the connections beside it. */
export interface ConnectorConnectFlowOptions {
  meta: ConnectorMeta;
  explainUnavailableActions?: boolean;
  /** The Allow edit switch of the connection being reconnected or edited, when known. */
  permissionLevel?: ConnectorPermissionLevel | null;
  /** The Allow delete switch of that connection, when known. */
  allowDelete?: boolean;
  /** The label of the connection being replaced, for the credentials dialog's wording. */
  connectionLabel?: string;
  /** Every connection of this provider the caller already holds here. */
  peerConnections?: ConnectorConnection[];
  /** Whether this deployment can connect a customer's own directory at all. */
  externalTenantLaneAvailable?: boolean;
  /** The customer directories already approved for this workspace. */
  approvedExternalTenants?: ExternalTenantConsent[];
}

/** What the connect flow hands back: the actions that start it and the dialogs it runs through. */
export interface ConnectorConnectFlowState {
  /** The OAuth driver, for its `busy` state and its cancel. */
  oauth: ReturnType<typeof useOAuthConnect>;
  /** Starts a connect; `connectionId` names a connection to re-connect, `null` asks for a new one. */
  startConnect: (connectionId: string | null) => Promise<void>;
  /** Opens the access dialog on the live connection `connectionId`, in its edit mode. */
  openAccessEdit: (connectionId: string) => void;
  /** Whether an access change on a live connection is being saved. */
  savingAccess: boolean;
  /** Every dialog the flow can open; render it once next to whatever starts the flow. */
  dialogs: ReactNode;
}

/**
 * The whole connect state machine of one connector — naming, the access choice, the method choice, the
 * tenant choice, the OAuth popup and the credentials form — lifted out of `ConnectorCard` so a
 * surface outside the directory (a Quality badge, an in-chat card) runs exactly the same flow instead
 * of growing a second copy of it. Scope comes from the surrounding `ConnectorScopeProvider`.
 */
export function useConnectorConnectFlow({
  meta,
  explainUnavailableActions = false,
  permissionLevel,
  allowDelete,
  connectionLabel,
  peerConnections = [],
  externalTenantLaneAvailable = false,
  approvedExternalTenants = [],
}: ConnectorConnectFlowOptions): ConnectorConnectFlowState {
  const { workspaceId, sharing = 'unknown' } = useConnectorScope();
  const [expanded, setExpanded] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessMode, setAccessMode] = useState<'connect' | 'edit'>('connect');
  const [tenantChoiceOpen, setTenantChoiceOpen] = useState(false);
  const [methodChoiceOpen, setMethodChoiceOpen] = useState(false);
  /**
   * Whether the sign-in already happened. The credentials form has to know, because it then opens on
   * the step the popup left rather than on its token step. Only Azure DevOps leaves one.
   */
  const [signedIn, setSignedIn] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupBusy, setSetupBusy] = useState(false);
  /**
   * The name, instruction and access the connect collected, held until the connect actually leaves. A
   * provider can put more dialogs between the form and the popup (the access choice, the tenant
   * choice), and the details have to survive each hop or the new connection is born unnamed anyway.
   * An access edit parks the connection it edits here too.
   */
  const [pendingDetails, setPendingDetails] =
    useState<ConnectionCreateDetails | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  const flowOwner = `${meta.id}:${workspaceId ?? ''}`;
  const ownerRef = useRef(flowOwner);
  ownerRef.current = flowOwner;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const credentialBusy = useRef(false);
  const setupInFlight = useRef(false);
  const invalidate = useInvalidateConnections();
  const queryClient = useQueryClient();
  const isOAuth = meta.authKind === 'oauth';
  const oauth = useOAuthConnect(meta.id, workspaceId, {
    explainUnavailableActions,
  });
  const hasCredentialsForm = Boolean(CREDENTIALS_FORMS[meta.id]);
  /**
   * How this provider can be connected, when there is more than one way. Both halves have to hold: the
   * words come from the table, and `oauth_configured` says the deployment actually holds the OAuth
   * application — without it the sign-in option would offer a popup that cannot open.
   */
  const connectionMethods = meta.oauth_configured
    ? CONNECTION_METHODS[meta.id]
    : undefined;
  /**
   * Whether a connect has to ask whose SharePoint it is first. A FRESH shared connect does, on a
   * provider that can reach another tenant. A reconnect never does: the connection already knows its
   * tenant and its OAuth application, so asking would offer to change something this flow cannot. A
   * personal workspace never does either — its own connection is always in our tenant.
   */
  const asksWhoseTenant = (connectionId: string | null): boolean =>
    TENANT_CHOICE_CONNECTORS.has(meta.id) &&
    sharing !== 'personal' &&
    connectionId === null;
  /**
   * Whether a fresh connect has to be named before it leaves. Required exactly where there is a choice
   * to make: the caller already reaches a connection of this provider here, so a second unnamed one
   * would leave both them and the agent with two identical options (§3). With none, neither field is
   * asked for.
   */
  const namingRequired = peerConnections.length > 0;
  /**
   * Seeds the access dialog. A reconnect or an edit starts from what the connection already allows, and
   * read-only when that is unknown, so an untouched confirm never widens it. Only a new connection
   * starts with everything on, and someone who wants less turns a switch off in the same dialog.
   */
  const initialAccess: ConnectorAccess =
    accessMode === 'edit' || pendingDetails?.connectionId != null
      ? storedConnectorAccess({ permissionLevel, allowDelete })
      : defaultConnectorAccess(meta.id);

  /**
   * Every connect starts at the access dialog. `details` is remembered rather than passed along,
   * because the access, method and tenant dialogs confirm later and would otherwise lose it.
   */
  const continueConnect = (
    connectionId: string | null,
    details: ConnectionCreateDetails | null,
  ): void => {
    setPendingDetails({ ...details, connectionId });
    setAccessMode('connect');
    setAccessOpen(true);
  };

  /**
   * Start a connect. `connectionId` names an existing connection to RE-connect — the grant then lands
   * on the connection the user is looking at instead of creating a second one beside it — and `null`
   * asks for a new one.
   *
   * A NEW connection beside an existing one is named first: the OAuth callback creates the row, so a
   * question asked after the popup would be a question asked too late.
   */
  const startConnect = async (connectionId: string | null): Promise<void> => {
    if (
      explainUnavailableActions &&
      (oauth.busy || setupInFlight.current || credentialBusy.current)
    ) {
      notify.warning(
        'A connection request is running. Finish or cancel sign-in before starting another connection.',
      );
      return;
    }
    if (connectionId === null && namingRequired) {
      oauth.clearError();
      setSetupOpen(true);
      return;
    }
    continueConnect(connectionId, null);
  };

  /**
   * Brings the naming form back when the service refuses a connect with a 409.
   *
   * That refusal names the connections still missing a name or an instruction, and this form is the
   * only place they can be filled in. Every connect has an extra hop — the access choice, and for
   * SharePoint the question of whose directory — which closes the form before the connect even leaves, so the refusal
   * used to arrive with nothing on screen that could act on it. The only move left was to press the
   * button again, which is exactly what happened on a live stand: nine identical attempts in eleven
   * seconds, every one refused for the same reason.
   *
   * Reopening also ends the loop, because the intermediate dialogs close with it: the retry lands on
   * the form that can fix the cause instead of on the button that cannot.
   */
  useEffect(() => {
    if (oauth.error?.status !== 409) return;
    setSetupOpen(true);
    setTenantChoiceOpen(false);
    setAccessOpen(false);
    setMethodChoiceOpen(false);
  }, [oauth.error?.status, oauth.error?.message]);

  /**
   * Everything an OAuth connect needs from this flow: the name, instruction and access it collected, plus
   * the connections already on screen. The poll needs that list to tell a NEW connection from the ones that
   * were there when the popup opened — without it a second workspace connection finished the instant it
   * started, on the strength of the first one.
   */
  const connectArgs = (details: ConnectionCreateDetails | null) => ({
    ...connectDetailsArgs(details),
    permissionLevel: details?.permissionLevel,
    allowDelete: details?.allowDelete,
    knownConnectionIds: peerConnections.map(
      (peer: ConnectorConnection) => peer.connectionId,
    ),
    personalWorkspace: sharing === 'personal',
  });

  /**
   * Names and instructions the connect form collected, plus any repair the service asked for, then the
   * access dialog the connect continues in. A refusal of the connect itself reopens this form (the 409
   * effect above).
   */
  const handleSetupConfirm = async ({
    details,
    peerPatches,
  }: ConnectionSetupResult): Promise<void> => {
    if (setupInFlight.current) {
      if (explainUnavailableActions)
        notify.warning(
          'Connection details are being saved. Wait for saving to finish.',
        );
      return;
    }
    setupInFlight.current = true;
    const ownsSetup = () => mounted.current && ownerRef.current === flowOwner;
    setSetupBusy(true);
    try {
      for (const [connectionId, patch] of peerPatches) {
        await api.updateConnectionProfile(
          meta.id,
          connectionId,
          patch,
          workspaceId,
        );
        if (!ownsSetup()) return;
      }
      if (!ownsSetup()) return;
      if (peerPatches.size > 0) invalidate();
      setSetupOpen(false);
      continueConnect(null, {
        label: details.label,
        usageHint: details.usageHint,
      });
    } catch (err) {
      if (!ownsSetup()) return;
      const message =
        err instanceof Error ? err.message : 'Could not save the connection';
      notify.error(message);
    } finally {
      setupInFlight.current = false;
      if (mounted.current) setSetupBusy(false);
    }
  };

  /**
   * Sign-in lane of the method choice: the ordinary OAuth connect, carrying the name and the
   * instruction the setup form collected — the form inside the card used to run this connect itself and
   * carried neither, which is exactly the unnamed second connection the service answers with a 409.
   *
   * A dismissed or refused popup keeps the dialog open, the way the tenant and access dialogs do,
   * so the choice can be retried or changed without starting over.
   */
  const handleSignInMethod = async (): Promise<void> => {
    setConnecting(true);
    try {
      const ok = await oauth.connect({
        connectionId: pendingDetails?.connectionId ?? null,
        ...connectArgs(pendingDetails),
      });
      if (!ok) return;
      setMethodChoiceOpen(false);
      if (connectionMethods?.signInContinuesInForm) {
        setSignedIn(true);
        setExpanded(true);
      }
    } finally {
      setConnecting(false);
    }
  };

  /** Token lane of the method choice: the provider's own form, opening on its token step. */
  const handleTokenMethod = (): void => {
    setMethodChoiceOpen(false);
    setSignedIn(false);
    setExpanded(true);
  };

  /**
   * Confirm handler for the tenant choice: only the corporate lane has an action, so this is the
   * ordinary fresh connect. A failed or dismissed popup keeps the dialog open, the same way the
   * access dialog does, so the user can retry without starting over.
   */
  const handleCorporateTenantConfirm = async (): Promise<void> => {
    setConnecting(true);
    try {
      const ok = await oauth.connect({
        connectionId: pendingDetails?.connectionId ?? null,
        ...connectArgs(pendingDetails),
      });
      if (ok) setTenantChoiceOpen(false);
    } finally {
      setConnecting(false);
    }
  };

  /**
   * Confirm handler for the customer lane: an ordinary connect, addressed to the approved directory.
   * The service picks the multi-tenant application from that directory and refuses one nobody has
   * approved, so the flow names the directory beside what every other connect names.
   *
   * It carries the name and the instruction the setup form collected, exactly like the corporate lane:
   * this is a second connection of the provider as often as any other, and a lane that left them behind
   * would create the unnamed one the service answers with a 409 — from a dialog with no field to fix it
   * in. There is no `connectionId` to carry, because a reconnect never opens this dialog.
   */
  const handleExternalTenantConfirm = async (
    tenantId: string,
  ): Promise<void> => {
    setConnecting(true);
    try {
      const ok = await oauth.connect({
        externalTenantId: tenantId,
        ...connectArgs(pendingDetails),
      });
      if (ok) setTenantChoiceOpen(false);
    } finally {
      setConnecting(false);
    }
  };

  /**
   * Fetches the link the customer's administrator has to follow. A failure is shown as a toast and
   * returns null, which leaves the dialog offering the button again rather than an empty box.
   */
  const requestApprovalLink = async (): Promise<string | null> => {
    try {
      const { consent_url } = await api.getAdminConsentUrl(
        meta.id,
        workspaceId,
      );
      return consent_url;
    } catch (err) {
      notify.error(
        err instanceof Error ? err.message : 'Failed to get the approval link',
      );
      return null;
    }
  };

  /**
   * Re-reads the connections so an approval granted a moment ago becomes visible. The presence query
   * keeps its answer for a while and never polls, so without an explicit re-read the screen's promise —
   * come back and it will say so — would rest on the person reopening the page late enough.
   */
  const checkForApproval = async (): Promise<void> => {
    await invalidate();
  };

  /**
   * What dismissing a busy dialog does: cancel the sign-in, when a sign-in is what it is busy with.
   * Anything else — saving a level, saving names — keeps the dialog locked until it finishes, because
   * that work cannot be taken back halfway.
   */
  const cancelSignIn = oauth.busy ? oauth.cancel : undefined;

  /**
   * Connect-time branch of the access dialog: remembers the choice, then moves on to the step this
   * provider needs next. A credential provider opens its method choice or its form, and the choice
   * travels in the credentials POST. An OAuth provider asks whose directory where it must, or opens the
   * popup with the choice on the authorize URL. Only a confirmed popup closes the dialog: a `false`
   * result (dismissed, never authorized, timed out) keeps it open with the confirm re-enabled.
   */
  const connectWithAccess = async (access: ConnectorAccess): Promise<void> => {
    const pending: ConnectionCreateDetails = { ...pendingDetails, ...access };
    setPendingDetails(pending);
    if (hasCredentialsForm || !isOAuth) {
      setAccessOpen(false);
      if (connectionMethods) setMethodChoiceOpen(true);
      else setExpanded(true);
      return;
    }
    if (asksWhoseTenant(pending.connectionId ?? null)) {
      setAccessOpen(false);
      setTenantChoiceOpen(true);
      return;
    }
    setConnecting(true);
    try {
      const ok = await oauth.connect({
        connectionId: pending.connectionId ?? null,
        ...connectArgs(pending),
      });
      if (ok) setAccessOpen(false);
    } finally {
      setConnecting(false);
    }
  };

  /**
   * Edit branch: writes the access straight onto the live connection. There is no OAuth leg because
   * the access is a gate on our side, so the connection keeps the token it already has. A failure
   * keeps the dialog open with the choice intact.
   */
  const saveAccess = async (access: ConnectorAccess): Promise<void> => {
    const connectionId = pendingDetails?.connectionId;
    if (!connectionId) return;
    setSavingAccess(true);
    try {
      await api.updateConnectionAccess(
        meta.id,
        connectionId,
        access,
        workspaceId,
      );
      invalidate();
      for (const queryKey of ACCESS_CACHE_KEYS)
        void queryClient.invalidateQueries({ queryKey });
      setAccessOpen(false);
    } catch (err) {
      notify.error(
        err instanceof Error
          ? err.message
          : `Could not change the ${meta.name} access`,
      );
    } finally {
      setSavingAccess(false);
    }
  };

  /** Routes the dialog's confirm to the flow that opened it. */
  const handleAccessConfirm = async (
    access: ConnectorAccess,
  ): Promise<void> => {
    if (accessMode === 'edit') {
      await saveAccess(access);
      return;
    }
    await connectWithAccess(access);
  };

  /** Opens the same access dialog on a live connection, in its edit mode. */
  const openAccessEdit = (connectionId: string): void => {
    setPendingDetails({ connectionId });
    setAccessMode('edit');
    setAccessOpen(true);
  };

  const CredentialsForm = CREDENTIALS_FORMS[meta.id];
  /**
   * Whether the credentials dialog is open. Owned by `expanded` alone (reset via onSaved/onCancel),
   * NOT by `connected`: an OAuth connect becomes live at the callback — before the form's
   * post-connect steps (the Azure DevOps org pick) have run — and unmounting on `connected` would
   * tear those steps down mid-flow.
   *
   * The form is keyed on `signedIn` because that is the one thing deciding which step it opens on, so
   * a form still mounted when the lane changes remounts rather than showing the step of the other
   * lane.
   */
  const showingForm = expanded && CredentialsForm !== undefined;
  /**
   * The credential landed: the dialog closes and the connections are re-read, since a saved credential
   * is what makes this connector connected.
   */
  const handleCredentialsSaved = (): void => {
    setExpanded(false);
    setSignedIn(false);
    setPendingDetails(null);
    if (!CREDENTIALS_REFRESH_CONNECTIONS.has(meta.id)) invalidate();
  };

  /** Backs out of the credential step. Closing the dialog is this and nothing else. */
  const handleCredentialsCancel = (): void => {
    setExpanded(false);
    setSignedIn(false);
    setPendingDetails(null);
  };

  /**
   * Which of two things the credential step is doing. A pending `connectionId` names an existing
   * connection, so the credential is going to replace the one it already holds; without one this is a
   * connection about to be created. The two are worth telling apart because a replacement creates
   * nothing and a fresh connect does, and the dialog is where somebody last gets to notice.
   */
  const replacingCredentials = pendingDetails?.connectionId != null;
  const credentialsHeading = replacingCredentials
    ? `Replace the ${meta.name} credentials`
    : `Connect ${meta.name}`;
  /** The visible line under the heading: which of the two this is, and what it does to the account. */
  const credentialsBlurb = replacingCredentials
    ? `${connectionLabel ?? meta.name} stays the same connection — nothing new is created beside it.`
    : `Enter the ${meta.name} credentials the assistant should use.`;
  /**
   * What a screen reader announces about the dialog. Deliberately not the line above: the description
   * is read out on open and the visible line is read again in the content, so the same sentence in both
   * places is heard twice. The sibling dialogs in this folder split them the same way.
   */
  const credentialsPurpose = replacingCredentials
    ? 'Enter the credential that replaces the one this connection holds.'
    : 'Enter the credential to connect with.';

  const dialogs = (
    <>
      <ConnectorAccessDialog
        open={accessOpen}
        onOpenChange={setAccessOpen}
        mode={accessMode}
        providerId={meta.id}
        providerName={meta.name}
        initialAccess={initialAccess}
        busy={connecting || savingAccess}
        onDismissWhileBusy={cancelSignIn}
        onConfirm={handleAccessConfirm}
      />

      <ConnectionSetupDialog
        key={`setup:${flowOwner}`}
        explainUnavailableActions={explainUnavailableActions}
        open={setupOpen}
        onOpenChange={(next) => {
          setSetupOpen(next);
          if (!next) oauth.clearError();
        }}
        providerName={meta.name}
        peers={peerConnections}
        personalWorkspace={sharing === 'personal'}
        conflictMessage={
          oauth.error?.status === 409 ? oauth.error.message : null
        }
        busy={setupBusy || oauth.busy}
        onDismissWhileBusy={cancelSignIn}
        onConfirm={handleSetupConfirm}
      />

      {connectionMethods && (
        <ConnectionMethodDialog
          key={`method:${flowOwner}`}
          explainUnavailableActions={explainUnavailableActions}
          open={methodChoiceOpen}
          onOpenChange={setMethodChoiceOpen}
          providerName={meta.name}
          signInWith={connectionMethods.signInWith}
          tokenLabel={connectionMethods.tokenLabel}
          busy={connecting || oauth.busy}
          onDismissWhileBusy={cancelSignIn}
          onChooseSignIn={handleSignInMethod}
          onChooseToken={handleTokenMethod}
        />
      )}

      {TENANT_CHOICE_CONNECTORS.has(meta.id) && (
        <SharePointTenantChoiceDialog
          open={tenantChoiceOpen}
          onOpenChange={setTenantChoiceOpen}
          externalTenantLaneAvailable={externalTenantLaneAvailable}
          approvedTenants={approvedExternalTenants}
          busy={connecting}
          onDismissWhileBusy={cancelSignIn}
          onConfirmCorporate={handleCorporateTenantConfirm}
          onConfirmExternal={handleExternalTenantConfirm}
          onRequestApprovalLink={requestApprovalLink}
          onCheckForApproval={checkForApproval}
        />
      )}
      {CredentialsForm && (
        <AppDialog
          open={showingForm}
          onOpenChange={(next) => {
            if (!next) {
              if (explainUnavailableActions && credentialBusy.current) {
                notify.warning(
                  'The connection request has already been sent and cannot be canceled. Wait for it to finish, then close this dialog.',
                );
                return;
              }
              handleCredentialsCancel();
            }
          }}
          title={credentialsHeading}
          description={credentialsPurpose}
          titleHidden
          showCloseButton={false}
          className="flex w-full max-h-[85vh] max-w-[var(--dialog-w-sm)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl"
        >
          {/*
            The form brings its own Cancel and Connect, so this box adds no footer of its own — only
            the heading. Every form carries a top margin sized for the card it used to sit in; the
            spacing it needs here is set on the form from out here, once, rather than in eleven files.
          */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-6 pb-6 [&>form]:mt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                {credentialsHeading}
              </h3>
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Close"
                  className="-mt-1 -mr-1 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
                >
                  <XIcon className="size-4" />
                </button>
              </DialogClose>
            </div>

            <p className="mt-2 text-compact leading-relaxed text-muted-foreground">
              {credentialsBlurb}
            </p>

            <CredentialsForm
              key={`${meta.id}:${workspaceId ?? ''}:${String(signedIn)}`}
              explainUnavailableActions={explainUnavailableActions}
              onBusyChange={(busy) => {
                credentialBusy.current = busy;
              }}
              details={pendingDetails ?? undefined}
              afterSignIn={signedIn}
              onSaved={handleCredentialsSaved}
              onCancel={handleCredentialsCancel}
            />
          </div>
        </AppDialog>
      )}
    </>
  );

  return { oauth, startConnect, openAccessEdit, savingAccess, dialogs };
}

/** Props of the standalone connect button. */
export interface ConnectorConnectFlowProps extends ConnectorConnectFlowOptions {
  /** The button's text; defaults to "Connect <name>". */
  label?: string;
  /** Extra classes for the button. */
  className?: string;
  /** A connection to re-connect in place; absent starts a new connection. */
  connectionId?: string | null;
  /** Disable new starts while keeping an active standalone flow mounted. */
  connectDisabled?: boolean;
}

/**
 * A single "Connect <provider>" button that runs the directory's own connect flow, for surfaces
 * outside the directory: a new connection, or a reconnect of `connectionId`. It must sit inside a `ConnectorScopeProvider`.
 */
export function ConnectorConnectFlow({
  label,
  className,
  connectionId = null,
  connectDisabled = false,
  ...options
}: ConnectorConnectFlowProps): JSX.Element {
  const flow = useConnectorConnectFlow(options);
  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (!connectDisabled) void flow.startConnect(connectionId);
        }}
        disabled={
          connectDisabled ||
          (!options.explainUnavailableActions && flow.oauth.busy)
        }
        className={className}
      >
        {flow.oauth.busy ? (
          <>
            <Loader2Icon className="size-3 animate-spin" aria-hidden="true" />
            <span>Waiting…</span>
          </>
        ) : (
          (label ?? `Connect ${options.meta.name}`)
        )}
      </button>
      {flow.dialogs}
    </>
  );
}
